// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { AccordionProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Tab from "@insite/mobius/Tab";
import TabGroup, { TabGroupPresentationProps } from "@insite/mobius/TabGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { color, MediaQuery } from "@rugby/XDL/Utilities";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    showDocuments = "showDocuments",
    showAttributes = "showAttributes",
}

type Props = WidgetProps & HasProduct;

export interface ProductDetailsSpecificationsStyles {
    specificationsAccordion?: AccordionProps;
    specificationsAccordionSection?: AccordionSectionPresentationProps;
    documentsContainer?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    attributeTypeWrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;

    // <XNG-Changes>
    specificationTypeWrapper?: InjectableCss;
    specificationTypeLabelText?: TypographyPresentationProps;
    specificationTypeValuesText?: TypographyPresentationProps;
    flexWrapper?: InjectableCss;
    tabGroupStyle?: TabGroupPresentationProps;
}

export const specificationsStyles: ProductDetailsSpecificationsStyles = {
    specificationsAccordion: {
        headingLevel: 2,
    },
    documentGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    documentIcon: {
        src: File,
        color: "text.link",
        css: css`
            margin-right: 10px;
        `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
        `,
    },
    // <XNG-Changes>
    flexWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
        `,
    },
    attributeTypeWrapper: {
        css: css`
            flex: 1 0 calc(50% - 30px);
            max-width: calc(50% - 30px);
            margin: 0 15px;

            display: flex;
            align-items: flex-start;

            font-size: 15px;
            letter-spacing: 0.01px;
            line-height: 18px;

            padding: 16px;
            border-top: 1px solid ${color("common.border")};
            &:nth-last-child(-n + 2) {
                border-bottom: 1px solid ${color("common.border")};
            }

            ${MediaQuery.lte("lg")} {
                flex-basis: 100%;
                max-width: 100%;
                margin: 0;
            }
        `,
    },
    tabGroupStyle: {
        cssOverrides: {
            tabContent: css`
                border-top: 0;
                border-bottom: 0;
            `,
            wrapper: css`
                li[class^="TabStyle"] {
                    min-width: 180px;
                    &[aria-selected="false"] {
                        color: ${getColor("secondary.main")};
                    }
                }
            `,
        },
    },
    attributeTypeLabelText: {
        weight: "bold",
        css: css`
            flex: 1;
            text-align: right;
            margin-right: 22px;
            ${MediaQuery.between(768, 1024)} {
                text-align: left;
            }
        `,
    },
    attributeTypeValuesText: {
        css: css`
            flex: 1;
        `,
    },
    specificationTypeWrapper: {
        css: css`
            flex: 1 0 100%;

            font-size: 15px;
            letter-spacing: 0.01px;
            line-height: 18px;

            padding: 16px;
            border-top: 1px solid ${color("common.border")};
            &:last-child {
                border-bottom: 1px solid ${color("common.border")};
            }
        `,
    },
    specificationTypeLabelText: {
        weight: "bold",
        css: css`
            flex-basis: 100%;
            text-align: left;
            margin-bottom: 16px;
            display: inline-flex;
        `,
    },
    specificationTypeValuesText: {
        css: css`
            flex-basis: 100%;
            body {
                background: ${getColor("common.white")};
            }
        `,
    },
};

const styles = specificationsStyles;

const ProductDetailsSpecifications: React.FC<Props> = ({
    product: { brand, attributeTypes, documents, specifications },
    fields: { showDocuments, showAttributes },
}) => {
    const renderingDocuments = showDocuments && documents && documents.length > 0;
    const renderingSpecifications = specifications && specifications.length > 0;
    const renderingAttributes = showAttributes && (brand || (attributeTypes && attributeTypes.length > 0));
    if (!renderingDocuments && !renderingAttributes && !specifications?.length) {
        return null;
    }

    const customTabs = [];

    if (renderingAttributes) {
        customTabs.push(
            <Tab
                data-test-selector="productDetails_specifications_documents"
                headline="Attributes"
                key="Attributes"
                tabKey="Attributes"
            >
                <StyledWrapper {...styles.flexWrapper}>
                    {brand && (
                        <StyledWrapper {...styles.attributeTypeWrapper}>
                            <Typography {...styles.attributeTypeLabelText}>{translate("Brand")}:&nbsp;</Typography>
                            <Typography {...styles.attributeTypeValuesText}>{brand.name}</Typography>
                        </StyledWrapper>
                    )}
                    {attributeTypes &&
                        attributeTypes.map(attributeType => (
                            <StyledWrapper {...styles.attributeTypeWrapper} key={attributeType.id.toString()}>
                                <Typography {...styles.attributeTypeLabelText}>
                                    {attributeType.label || attributeType.name}:&nbsp;
                                </Typography>
                                <Typography {...styles.attributeTypeValuesText}>
                                    {attributeType
                                        .attributeValues!.map(attributeValue => attributeValue.valueDisplay)
                                        .join(", ")}
                                </Typography>
                            </StyledWrapper>
                        ))}
                </StyledWrapper>
            </Tab>,
        );
    }

    if (renderingSpecifications) {
        customTabs.push(
            <Tab
                data-test-selector="productDetails_specifications_specification"
                headline="Specifications"
                key="Specifications"
                tabKey="Specifications"
            >
                <StyledWrapper {...styles.flexWrapper}>
                    {specifications?.map(specification => {
                        return (
                            <StyledWrapper {...styles.specificationTypeWrapper} key={specification.id.toString()}>
                                <Typography {...styles.specificationTypeLabelText}>
                                    {specification.nameDisplay}:&nbsp;
                                </Typography>
                                <Typography {...styles.specificationTypeValuesText}>
                                    {parse(transformContentHtml(specification.htmlContent), parserOptions)}
                                </Typography>
                            </StyledWrapper>
                        );
                    })}
                </StyledWrapper>
            </Tab>,
        );
    }

    if (renderingDocuments) {
        customTabs.push(
            <Tab
                data-test-selector="productDetails_specifications_documents"
                headline="Documents"
                key="Documents"
                tabKey="Documents"
            >
                <GridContainer {...styles.documentsContainer}>
                    {documents!.map(document => (
                        <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                            <Link
                                href={document.filePath}
                                target="_new"
                                {...styles.documentLink}
                                data-test-selector={`productDetails_${document.id}`}
                            >
                                <IconMemo {...styles.documentIcon} />
                                <Typography {...styles.documentNameText}>{document.name}</Typography>
                            </Link>
                        </GridItem>
                    ))}
                </GridContainer>
            </Tab>,
        );
    }

    return <TabGroup {...styles.tabGroupStyle}>{customTabs}</TabGroup>;
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsSpecifications),
    definition: {
        displayName: "Specification",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.showDocuments,
                displayName: "Show Documents",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 2,
            },
        ],
    },
};

export default widgetModule;
